import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'rect' | 'circle';
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '', variant = 'rect' }) => {
  const baseClasses = 'animate-pulse bg-gray-100';
  const variantClasses = {
    text: 'h-4 w-full rounded',
    rect: 'rounded-lg',
    circle: 'rounded-full',
  };

  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`} />
  );
};

const GameCardSkeleton = () => (
  <div className="shrink-0 w-44 md:w-48 h-14 md:h-16 bg-white rounded-full border border-gray-100 shadow-sm flex items-center justify-between px-5 my-5">
    <Skeleton className="w-6 h-6 md:w-7 md:h-7" variant="circle" />
    <Skeleton className="w-12 h-4" />
    <Skeleton className="w-6 h-6 md:w-7 md:h-7" variant="circle" />
  </div>
);

const NewsCardSkeleton = () => (
  <div className="flex-shrink-0 w-[300px] bg-white rounded-[28px] overflow-hidden shadow-sm border border-gray-100">
    <div className="aspect-video relative">
      <Skeleton className="w-full h-full !bg-gray-100" />
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <Skeleton className="w-7 h-7 !bg-gray-200" variant="circle" />
        <Skeleton className="w-16 h-2 !bg-gray-200" />
      </div>
      <div className="absolute bottom-4 left-4 right-4 space-y-2">
        <Skeleton className="w-20 h-3 !bg-gray-200" />
        <Skeleton className="w-full h-4 !bg-gray-200" />
      </div>
    </div>
  </div>
);

const PlayerRowSkeleton = () => (
  <div className="flex items-center gap-3 md:gap-4 bg-white rounded-[18px] md:rounded-[24px] p-3 md:p-4 border border-gray-100 shadow-sm">
    <Skeleton className="w-8 h-8 rounded-xl" />
    <Skeleton className="w-12 h-12" variant="circle" />
    <div className="flex-1 space-y-2">
      <Skeleton className="w-24 h-4" />
      <Skeleton className="w-32 h-3" />
    </div>
    <Skeleton className="w-16 h-4" />
  </div>
);

import { XlevenLogo } from './XlevenLogo';
import { Search } from 'lucide-react';

export const HomeSkeleton = ({ onOpenSearch }: { onOpenSearch?: () => void }) => (
  <div className="min-h-screen bg-white">
    {/* Header - Always visible with real components */}
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-50 px-4 h-14 flex items-center justify-between md:hidden">
      <XlevenLogo />
      <button onClick={onOpenSearch} className="p-2 text-slate-900">
        <Search size={20} />
      </button>
    </header>

    {/* Desktop Sidebar Placeholder for Skeleton */}
    <div className="hidden md:flex fixed left-0 top-0 bottom-0 w-20 lg:w-64 border-r border-gray-100 bg-white z-50 flex-col py-6 items-center lg:items-stretch lg:px-4 gap-8">
      <div className="px-2 lg:px-4"><XlevenLogo /></div>
      <div className="flex flex-col gap-4">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex items-center gap-4 px-4 py-2">
            <Skeleton className="w-6 h-6" variant="circle" />
            <Skeleton className="hidden lg:block w-24 h-4" />
          </div>
        ))}
      </div>
    </div>

    {/* Games Strip Skeleton */}
    <div className="bg-white pt-4 pb-6">
      <div className="flex gap-5 px-4 overflow-hidden">
        {[1, 2, 3].map((i) => <GameCardSkeleton key={i} />)}
      </div>
    </div>

    <div className="p-4 md:p-6 lg:p-8 space-y-12">
      {/* Recommended News Skeleton */}
      <section>
        <div className="flex justify-between mb-6">
          <Skeleton className="w-40 h-6" />
          <Skeleton className="w-12 h-4" />
        </div>
        <div className="flex gap-4 overflow-hidden">
          {[1, 2].map((i) => <NewsCardSkeleton key={i} />)}
        </div>
      </section>

      {/* Market Values Skeleton */}
      <section>
        <div className="flex justify-between mb-6">
          <Skeleton className="w-32 h-6" />
          <Skeleton className="w-12 h-4" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <PlayerRowSkeleton key={i} />)}
        </div>
      </section>
    </div>
  </div>
);

export const NewsSkeleton = () => (
  <div className="bg-white min-h-screen">
    {/* Header & Date Strip Skeleton */}
    <header className="sticky top-0 z-40 bg-white/95 px-4 h-14 flex items-center justify-between md:hidden">
      <div className="w-24 h-6 bg-gray-100 rounded animate-pulse" />
      <Skeleton className="w-8 h-8" variant="circle" />
    </header>
    <div className="sticky top-[49px] md:top-0 border-b border-gray-100 px-4 py-2 bg-white/95 backdrop-blur-md">
      <div className="flex gap-4 overflow-hidden">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="shrink-0 w-12 h-16 rounded-2xl border border-gray-50 flex flex-col items-center justify-center gap-2">
            <Skeleton className="w-6 h-2" />
            <Skeleton className="w-4 h-4" />
            <Skeleton className="w-6 h-2" />
          </div>
        ))}
      </div>
    </div>

    {/* News Feed Skeleton */}
    <div className="max-w-[700px] mx-auto border-x border-gray-50">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 border-b border-gray-50 space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10" variant="circle" />
            <div className="space-y-2">
              <Skeleton className="w-24 h-3" />
              <Skeleton className="w-16 h-2" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="w-full h-4" />
            <Skeleton className="w-3/4 h-4" />
          </div>
          <Skeleton className="w-full aspect-video rounded-2xl" />
        </div>
      ))}
    </div>
  </div>
);
